// Chakra imports
import {
	Alert,
	AlertDescription,
	AlertIcon,
	Button,
	Flex,
	FormControl,
	SimpleGrid,
	Text,
	useColorModeValue,
} from '@chakra-ui/react';
import { userService } from 'api/services/userService';
import Card from 'components/card/Card.js';
import InputField from 'components/fields/InputField';
import { ALERT, ERROR, SUCCESS } from 'constant/messages';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userActions } from 'store/user';

export default function Information(props) {
	const { ...rest } = props;
	const TOKEN = useSelector((state) => state.auth.tokenId);
	const textColorPrimary = useColorModeValue('gray.900', 'gray.100');
	const textColorSecondary = useColorModeValue('gray.900', 'gray.100');
	const brandColor = useColorModeValue('brand.500', 'gray.100');
	const inputBorder = useColorModeValue('gray.900', 'gray.100');
	const cardBg = useColorModeValue('gray.100', 'gray.500');

	const userDataState = useSelector((state) => state.user);
	const { userFirstName, userLastName, userEmail, userUsername } = useSelector(
		(state) => state.user
	);

	const dispatch = useDispatch();

	const [isLoading, setIsLoading] = useState(false);

	const [profile, setProfile] = useState({
		firstName: userFirstName,
		lastName: userLastName,
		email: userEmail,
		username: userUsername,
	});

	const [message, setMessage] = useState({
		state: false,
		status: '',
		description: '',
	});

	const validateEmail = (email) => {
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailPattern.test(email);
	};

	const handleUpdateProfile = async () => {
		let isValidEmail = validateEmail(profile.email);
		if (!isValidEmail) {
			setProfile((prevState) => ({ ...prevState, email: userEmail }));
			handleMessage('error', ERROR.VALID_EMAIL);
			return;
		}

		try {
			setIsLoading(true);

			const result = await userService.updateProfile(TOKEN, profile);
			if (result.status === 200 && result.data.status === 'SUCCESS') {
				handleMessage('success', SUCCESS.PROFILE_UPDATED);
				dispatch(
					userActions.setUserData({
						...userDataState,
						userUsername: profile.username,
						userEmail: profile.email,
						userFirstName: profile.firstName,
						userLastName: profile.lastName,
					})
				);
			} else {
				handleMessage('error', ALERT.ERROR_TITLE + '. ' + ALERT.ERROR_SUBTITLE);
			}
		} catch (error) {
			handleMessage('error', ALERT.ERROR_TITLE + '. ' + ALERT.ERROR_SUBTITLE);
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleMessage = (status, description) => {
		setMessage({
			state: true,
			status: status,
			description: description,
		});
	};

	const handleFocus = () => {
		setMessage({
			state: false,
			status: '',
			description: '',
		});
	};

	return (
		<Card bg={cardBg} boxShadow='md' mb='20px' {...rest}>
			<FormControl>
				<Flex direction='column' mb='30px' ms='10px'>
					<Text fontSize='xl' color={textColorPrimary} fontWeight='bold'>
						Configuración de perfil
					</Text>
					<Text fontSize='md' color={textColorSecondary}>
						Aquí puedes hacer cambios a tu información de usuario.
					</Text>
					{!isLoading && message.state && (
						<Alert mt='15px' status={message.status || 'error'}>
							<AlertIcon />
							<AlertDescription>{message.description}</AlertDescription>
						</Alert>
					)}
				</Flex>

				<SimpleGrid
					columns={{ sm: 1, md: 2 }}
					spacing={{ base: '20px', xl: '20px' }}
				>
					<InputField
						_focus={{ borderWidth: 2, borderColor: brandColor }}
						borderColor={inputBorder}
						bg={cardBg}
						mb='0px'
						me='30px'
						id='user'
						label='Usuario'
						placeholder={userUsername}
						value={profile.username}
						onFocus={handleFocus}
						onChange={(e) => {
							setProfile((prevState) => ({
								...prevState,
								username: e.target.value,
							}));
						}}
					/>
					<InputField
						_focus={{ borderWidth: 2, borderColor: brandColor }}
						borderColor={inputBorder}
						bg={cardBg}
						mb='0px'
						id='email'
						label='Correo electrónico'
						placeholder={userEmail}
						value={profile.email}
						onFocus={handleFocus}
						onChange={(e) => {
							setProfile((prevState) => ({
								...prevState,
								email: e.target.value,
							}));
						}}
					/>
					<InputField
						_focus={{ borderWidth: 2, borderColor: brandColor }}
						borderColor={inputBorder}
						bg={cardBg}
						mb='20px'
						me='30px'
						id='first_name'
						label='Nombre(s)'
						placeholder={userFirstName}
						value={profile.firstName}
						onFocus={handleFocus}
						onChange={(e) => {
							setProfile((prevState) => ({
								...prevState,
								firstName: e.target.value,
							}));
						}}
					/>
					<InputField
						_focus={{ borderWidth: 2, borderColor: brandColor }}
						borderColor={inputBorder}
						bg={cardBg}
						mb='20px'
						id='last_name'
						label='Apellido(s)'
						placeholder={userLastName}
						value={profile.lastName}
						onFocus={handleFocus}
						onChange={(e) => {
							setProfile((prevState) => ({
								...prevState,
								lastName: e.target.value,
							}));
						}}
					/>
				</SimpleGrid>
			</FormControl>
			<Button
				isLoading={isLoading}
				isDisabled={
					profile.username === userUsername &&
					profile.email === userEmail &&
					profile.firstName === userFirstName &&
					profile.lastName === userLastName
				}
				borderRadius='16px'
				minW='183px'
				h='44px'
				ms='auto'
				mt='33px'
				variant='brand'
				fontSize='sm'
				fontWeight='500'
				onClick={handleUpdateProfile}
			>
				Actualizar perfil
			</Button>
		</Card>
	);
}
